import {
  AIMS_STATUS,
  ROLES,
  OUTLOOK_WARNINGS,
  AIMS_AVAILABILITY_MESSAGE_TYPES,
  AIMS_NOT_AVAILABLE_MESSAGE,
  RTMS_REQUESTER_DEFAULT_NAME,
  AUTO_START_MODES,
  SESSION_STORAGE_KEYS,
} from "../constants";
import LogManager from "./logManager";

/**
 * Clear the cached summaries from sessionStorage on app initialization (refresh),
 * so a fresh session doesn't render stale summary data.
 */
export const clearSummariesCache = () => {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.SUMMARY_CACHE);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.LAST_EVALUATED_KEY);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.RAW_SUMMARIES);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.IS_END_OF_DATA);
  LogManager.info("Cleared summaries cache on app initialization");
};

/**
 * Map an AIMS availability API response to the flat set of state values the
 * caller should apply. Returns an all-defaults result when the response is null.
 *
 * @param {object|null} aimsAvailability - Parsed AIMS availability API response.
 * @returns {object} State values to apply — see the `result` object below.
 */
export const handleAimsAvailabilityResult = (aimsAvailability) => {
  const result = {
    role: null,
    alias: null,
    hasOutlookWarning: false,
    hasGermanyAttendee: false,
    aimsStatus: AIMS_STATUS.AVAILABLE,
    aimsNotAvailableMessage: null,
    autoStartMode: null,
    inviteeAliases: null,
    externalInvitees: null,
  };

  if (!aimsAvailability) {
    LogManager.warn("The aimsAvailability value is null, show exception view.");
    result.aimsStatus = AIMS_STATUS.EXCEPTION;
    return result;
  }

  const {
    result: AimsAvailableResult,
    message: AimsAvailableMessage,
    warningFromOutlook,
    role,
    alias,
    hasGermanyAttendee,
    autoStartMode,
    invitees: inviteeAliases,
    externalInvitees,
  } = aimsAvailability;

  // Set user role information
  result.role = role;
  result.alias = alias;
  result.hasOutlookWarning = Object.values(OUTLOOK_WARNINGS).includes(warningFromOutlook);
  result.hasGermanyAttendee = hasGermanyAttendee || false;
  result.autoStartMode = autoStartMode;
  result.inviteeAliases = inviteeAliases || null;
  result.externalInvitees = externalInvitees || null;
  LogManager.info(`User alias: ${alias}, role: ${role}`);

  if (AimsAvailableResult === AIMS_STATUS.TRANSCRIPT_AVAILABLE) {
    LogManager.info("Transcript is available for this meeting.");
    result.aimsStatus = AIMS_STATUS.TRANSCRIPT_AVAILABLE;
    LogManager.info(`Auto start mode: ${autoStartMode}`);
  } else if (AimsAvailableResult === AIMS_STATUS.TRANSCRIPT_NOT_AVAILABLE) {
    LogManager.info("Transcript is not available for this meeting.");
    result.aimsStatus = AIMS_STATUS.TRANSCRIPT_NOT_AVAILABLE;
  } else if (AimsAvailableResult === AIMS_STATUS.AVAILABLE) {
    if (result.hasOutlookWarning) {
      LogManager.info("This meeting has MultiMeetingFoundFromOutlook/MeetingNotFoundFromOutlook warning.");
      result.aimsStatus = AIMS_STATUS.SUMMARY_TO_ORGANIZER;
    } else {
      LogManager.info("This is low risk meeting.");
      result.aimsStatus = AIMS_STATUS.AVAILABLE;
    }
  } else if (AimsAvailableResult === AIMS_STATUS.NOT_AVAILABLE) {
    LogManager.info("This is AimsNotAvailable meeting.");
    result.aimsStatus = AIMS_STATUS.NOT_AVAILABLE;
    if (AimsAvailableMessage === AIMS_AVAILABILITY_MESSAGE_TYPES.WORK_COUNCIL) {
      result.aimsNotAvailableMessage = AIMS_NOT_AVAILABLE_MESSAGE.REGIONS;
    } else if (AimsAvailableMessage === AIMS_AVAILABILITY_MESSAGE_TYPES.PERFORMANCE_REVIEW) {
      result.aimsNotAvailableMessage = AIMS_NOT_AVAILABLE_MESSAGE.PERFORMANCE;
    }
  } else {
    LogManager.warn("Unknown AimsAvailableResult value:", AimsAvailableResult);
    result.aimsStatus = AIMS_STATUS.EXCEPTION;
  }

  return result;
};

/**
 * Decide, for an active RTMS session, whether it was started by someone other
 * than the current user and what name to display for the requester.
 *
 * Pure decision — no state setters, no I/O.
 *
 * @param {object} params
 * @param {object} params.userContext - Zoom user context (role)
 * @param {object|null} params.aimsAvailability - AIMS availability response (alias, result, autoStartMode)
 * @param {object} params.sessionStatus - RTMS session status (rtmsRequesterAlias, rtmsRequesterName)
 * @returns {{ isStartedByOther: boolean, requesterName: string|null }}
 *   requesterName is only meaningful when isStartedByOther is true.
 */
export const resolveRtmsStartedByOther = ({ userContext, aimsAvailability, sessionStatus }) => {
  if (userContext.role === ROLES.ATTENDEE) {
    return { isStartedByOther: true, requesterName: sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME };
  }
  // Per-host multi-stream: this host owns their own stream, so it is never "started by other".
  if (aimsAvailability && aimsAvailability.result === AIMS_STATUS.TRANSCRIPT_AVAILABLE
      && aimsAvailability.autoStartMode === AUTO_START_MODES.MULTIPLE) {
    return { isStartedByOther: false, requesterName: null };
  }
  if (!sessionStatus.rtmsRequesterAlias) {
    return { isStartedByOther: true, requesterName: RTMS_REQUESTER_DEFAULT_NAME };
  }
  if (!aimsAvailability?.alias) {
    return { isStartedByOther: true, requesterName: sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME };
  }
  if (sessionStatus.rtmsRequesterAlias !== aimsAvailability.alias) {
    return { isStartedByOther: true, requesterName: sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME };
  }
  return { isStartedByOther: false, requesterName: null };
};

/**
 * Decide whether transcription should auto-start for this host.
 *
 * Pure decision — no state setters, no I/O. Covers both auto-start modes:
 * - MULTIPLE (per-host): auto-starts regardless of an existing session.
 * - SINGLE: auto-starts only when there is no active session yet.
 *
 * @param {object} params
 * @param {object|null} params.aimsAvailability - AIMS availability response (result, enableAutoStart, autoStartMode)
 * @param {boolean} params.hasActiveSession - Whether an RTMS session is already active
 * @returns {boolean}
 */
export const canAutoStartTranscription = ({ aimsAvailability, hasActiveSession }) => {
  if (!aimsAvailability
      || aimsAvailability.result !== AIMS_STATUS.TRANSCRIPT_AVAILABLE
      || !aimsAvailability.enableAutoStart) {
    return false;
  }
  if (aimsAvailability.autoStartMode === AUTO_START_MODES.MULTIPLE) {
    return true;
  }
  if (aimsAvailability.autoStartMode === AUTO_START_MODES.SINGLE) {
    return !hasActiveSession;
  }
  return false;
};
